/**
 * KillRunConfirmation — confirmation dialog for stopping a run.
 *
 * Shows a blocking modal with clear messaging about the consequences of killing
 * a run. Includes suggestion to use Pause for temporary breaks. Rendered at
 * shell level to ensure proper z-index layering above all UI elements.
 */

import type React from "react";
import { fonts, inkColors, statusColors, surfaceColors } from "../tokens";

interface KillRunConfirmationProps {
  /** When true, render the dialog. */
  open: boolean;
  /** Handler when user confirms kill intent. */
  onConfirm: () => void | Promise<void>;
  /** Handler when user cancels (wants to keep running). */
  onCancel: () => void;
  /** When true, disable buttons and show loading state. */
  confirming?: boolean;
}

export const KillRunConfirmation: React.FC<KillRunConfirmationProps> = ({
  open,
  onConfirm,
  onCancel,
  confirming = false,
}) => {
  if (!open) return null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <dialog
      onClick={onCancel}
      onKeyDown={handleKeyDown}
      open
      style={{
        position: "fixed",
        inset: "0",
        background: "rgba(0, 0, 0, 0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
        border: "none",
        margin: "auto",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="document"
        style={{
          background: surfaceColors.paper,
          borderRadius: "8px",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.16)",
          padding: "24px",
          maxWidth: "480px",
          width: "100%",
          fontFamily: fonts.body,
        }}
      >
        <div
          style={{
            fontSize: "18px",
            fontWeight: 700,
            color: inkColors.ink,
            marginBottom: "12px",
            lineHeight: "1.4",
          }}
        >
          Stop this run?
        </div>
        <div
          style={{
            fontSize: "13px",
            fontWeight: 400,
            color: inkColors.ink2,
            marginBottom: "16px",
            lineHeight: "1.5",
          }}
        >
          This cancels the current drafting run. The run stays open and sections already drafted
          are kept, but the run cannot be resumed.
        </div>
        <div
          style={{
            fontSize: "12px",
            fontWeight: 400,
            color: inkColors.ink4,
            background: "var(--surface-sunken)",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "20px",
            lineHeight: "1.5",
            borderLeft: `3px solid ${statusColors.warn}`,
          }}
        >
          <strong>Only want a break?</strong> Use Pause instead — it stops at the next safe
          checkpoint and use resume from there.
        </div>
        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={confirming}
            style={{
              fontFamily: fonts.body,
              fontSize: "12px",
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: "4px",
              border: `1px solid ${surfaceColors.line}`,
              background: "transparent",
              color: inkColors.ink2,
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              transition: "all 150ms",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              opacity: confirming ? 0.5 : 1,
            }}
          >
            Keep running
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirming}
            style={{
              fontFamily: fonts.body,
              fontSize: "12px",
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: "4px",
              border: "none",
              background: statusColors.danger,
              color: "var(--surface)",
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              transition: "all 150ms",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              opacity: confirming ? 0.5 : 1,
            }}
          >
            Yes, stop the run
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default KillRunConfirmation;
